import { Button, makeStyles, Text } from '@fluentui/react-components';
import { useAutofocus } from '@utils';
import {
  Report,
  ReportResponseType,
  DangerMailIcon,
  SafeMailIcon,
  DangerReportGif,
  SafeReportGif,
} from '@utils-global';
import { useTranslation } from 'react-i18next';
import { ChevronDoubleLeft16Regular } from '@fluentui/react-icons';
import { useIsV2 } from '@data';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '16px',
  },
  v2Wrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '16px',
    background: 'linear-gradient(to bottom right, #091731, #1D305A)',
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
  },
  v2Title: {
    color: '#ffffff',
    textAlign: 'center',
  },
  v2Description: {
    color: '#E0E0E0',
    textAlign: 'center',
  },
  icon: {
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '24px',
  },
  v2Gif: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '60%',
  },
  title: {
    color: '#14141E',
  },
  description: {
    color: '#706F6F',
  },
  backButton: {
    marginTop: '24px',
    alignSelf: 'center',
  },
  v2BackButton: {
    color: '#ffffff',
    padding: '0px',
    justifyContent: 'flex-start',
    position: 'absolute',
    top: '20px',
    left: '30px',
  },
});

const reportResponseTypeIconMap = {
  [ReportResponseType.SAFE]: SafeMailIcon,
  [ReportResponseType.SIMULATION]: SafeMailIcon,
  [ReportResponseType.ACTUAL_THREAT]: DangerMailIcon,
  [ReportResponseType.THREAT]: DangerMailIcon,
};

const reportResponseTypeGifMap = {
  [ReportResponseType.ACTUAL_THREAT]: DangerReportGif,
  [ReportResponseType.THREAT]: DangerReportGif,
  [ReportResponseType.SAFE]: SafeReportGif,
  [ReportResponseType.SIMULATION]: SafeReportGif,
};

type ReportResultProps = {
  report: Report;
  onBack?: () => void;
};

export const ReportResult = ({
  report: { Title, Description, Type },
  onBack,
}: ReportResultProps) => {
  const styles = useStyles();
  const { targetRef } = useAutofocus();
  const { t } = useTranslation();
  const isV2 = useIsV2();

  const Icon = reportResponseTypeIconMap[Type];
  const Gif = reportResponseTypeGifMap[Type];

  if (isV2) {
    return (
      <section
        tabIndex={-1}
        ref={targetRef}
        className={styles.v2Wrapper}
        aria-label="Report result page"
      >
        {onBack && (
          <Button
            appearance="transparent"
            icon={<ChevronDoubleLeft16Regular aria-hidden="true" />}
            onClick={onBack}
            className={styles.v2BackButton}
          >
            {t('reportPage.result.back')}
          </Button>
        )}
        <img src={Gif} alt="Danger report" className={styles.v2Gif} aria-hidden="true" />
        <Text weight="semibold" size={600} className={styles.v2Title} role="heading">
          {Title}
        </Text>
        <Text weight="regular" size={300} className={styles.v2Description}>
          {Description}
        </Text>
      </section>
    );
  }
  return (
    <section
      tabIndex={-1}
      ref={targetRef}
      className={styles.wrapper}
      aria-label="Report result page"
    >
      <Icon className={styles.icon} aria-hidden="true" />
      <Text weight="bold" color="#14141E" size={300} className={styles.title} role="heading">
        {Title}
      </Text>
      <Text weight="semibold" size={300} className={styles.description}>
        {Description}
      </Text>
    </section>
  );
};
